import {
  CarePractitionerFragment,
  useCarePractitionersByDoctorIdsQuery,
} from "@components/Booking/__generated__/care-practitioners-by-doctor-ids.type";
import {LocSlotPair} from "@components/Locations/usePairLocsWithSlots";
import {useMemo} from "react";
import {QueryState} from "src/hooks/useQueryController";
import {LocationFragment} from "src/queries/useFetchSlotsInArea";
import {compact} from "src/utils/arrays";

type LocWithSlotsAndDoctor = LocSlotPair<LocationFragment> & {
  doctor: CarePractitionerFragment;
};

/**
 * Appends a `{doctor: CarePractitionerFragment}` to each slot group
 */
export const useAppendDoctorsToSlotGroups = ({
  slotsQuery,
  skip,
}: {
  slotsQuery: QueryState<{
    initialLocation: LocSlotPair<LocationFragment>;
    nearbyLocations: LocSlotPair<LocationFragment>[];
    availabilityIsLimited?: boolean | undefined;
  }>;
  skip?: boolean;
}) => {
  /**
   * Flatten all of the slot groups
   */
  const allSlotGroups = useMemo(() => {
    if (skip) return null;
    return [slotsQuery.data.initialLocation, ...slotsQuery.data.nearbyLocations].filter(
      group => group.slots.length > 0,
    );
  }, [slotsQuery.data.initialLocation, slotsQuery.data.nearbyLocations, skip]);

  /**
   * Get all of the unique doctor ids from slot groups
   */
  const allDoctorIds = useMemo(() => {
    if (!allSlotGroups) return null;
    const hashMap = allSlotGroups.reduce<Record<string, boolean>>((map, slotGroup) => {
      slotGroup.slots.forEach(s => {
        map[s.doctorId] = true;
      });
      return map;
    }, {});
    return Object.keys(hashMap);
  }, [allSlotGroups]);

  /**
   * Fetch all of the doctors
   */
  const doctorsQuery = useCarePractitionersByDoctorIdsQuery({
    variables: {
      doctorIds: allDoctorIds || [],
    },
    skip: !allDoctorIds || allDoctorIds.length < 1,
  });

  /**
   * Make a list of slot groups, grouped by location and doctor.
   */
  const slotGroups = useMemo(() => {
    if (
      !allSlotGroups ||
      !doctorsQuery.data ||
      !Array.isArray(doctorsQuery.data?.carePractitionersByDoctorIds)
    )
      return [];

    return compact(doctorsQuery.data.carePractitionersByDoctorIds).reduce<LocWithSlotsAndDoctor[]>(
      (allDoctors, doctor) => {
        return [
          ...allDoctors,
          ...allSlotGroups.reduce<LocWithSlotsAndDoctor[]>((groupsForThisDoctor, slotGroup) => {
            const slotsForThisDoctor = slotGroup.slots.filter(s => s.doctorId === doctor.id);

            if (slotsForThisDoctor.length > 0) {
              return [
                ...groupsForThisDoctor,
                {
                  ...slotGroup,
                  doctor,
                  soonestSlot: slotsForThisDoctor[0],
                  slots: slotsForThisDoctor,
                },
              ];
            }

            return groupsForThisDoctor;
          }, []),
        ];
      },
      [],
    );
  }, [doctorsQuery.data, allSlotGroups]);

  return {loading: doctorsQuery.loading, data: slotGroups};
};
